/**
 * @fileoverview FRED Series Registry
 * Central registry of all FRED series used in CAP.IO.
 * @module store/fred/registry
 *
 * CONTRACT:
 * - All series IDs must be registered here
 * - Registry provides metadata for UI display and categorization
 * - Use getSeriesConfig() to look up series by key
 * - Use listSeriesConfigs() to enumerate available series
 */

/**
 * Series categories for organization and filtering.
 * @typedef {"liquidity"|"rates"|"credit"|"fx"|"labor"|"housing"|"inflation"|"growth"|"money"|"other"} FredSeriesCategory
 */

/**
 * Data frequency codes.
 * @typedef {"D"|"W"|"M"|"Q"|"A"} FredFrequency
 */

/**
 * Configuration for a registered FRED series.
 * @typedef {Object} FredSeriesConfig
 * @property {string} id - FRED series ID
 * @property {string} key - Internal key for lookup (e.g., "WALCL", "TGA")
 * @property {string} label - Human-readable label for UI display
 * @property {FredSeriesCategory} category - Category for filtering
 * @property {FredFrequency} defaultFrequency - Expected data frequency
 * @property {string} [units] - Units description
 * @property {string} [notes] - Additional notes or context
 */

/**
 * FRED Series Registry
 * Maps internal keys to series configurations.
 * 
 * Naming convention:
 * - Key is the internal reference (often same as FRED ID, but can be aliased)
 * - ID is the actual FRED series identifier
 *
 * @type {Record<string, FredSeriesConfig>}
 */
export const FRED_SERIES_REGISTRY = {
  // ═══════════════════════════════════════════════════════════════════════════
  // LIQUIDITY - Fed Balance Sheet & Money Markets
  // ═══════════════════════════════════════════════════════════════════════════
  
  WALCL: {
    id: "WALCL",
    key: "WALCL",
    label: "Fed Total Assets",
    category: "liquidity",
    defaultFrequency: "W",
    units: "Millions of Dollars",
    notes: "Federal Reserve total assets (balance sheet size)",
  },
  
  TGA: {
    id: "WTREGEN",
    key: "TGA",
    label: "Treasury General Account",
    category: "liquidity",
    defaultFrequency: "W",
    units: "Millions of Dollars",
    notes: "Treasury cash balance at the Fed",
  },
  
  WTREGEN: {
    id: "WTREGEN",
    key: "WTREGEN",
    label: "Treasury General Account",
    category: "liquidity",
    defaultFrequency: "W",
    units: "Millions of Dollars",
    notes: "Alias for TGA",
  },
  
  RRP: {
    id: "RRPONTSYD",
    key: "RRP",
    label: "Overnight Reverse Repo",
    category: "liquidity",
    defaultFrequency: "D",
    units: "Billions of Dollars",
    notes: "Fed overnight reverse repo facility usage",
  },
  
  RRPONTSYD: {
    id: "RRPONTSYD",
    key: "RRPONTSYD",
    label: "Overnight Reverse Repo",
    category: "liquidity",
    defaultFrequency: "D",
    units: "Billions of Dollars",
    notes: "Fed overnight reverse repo facility usage",
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RATES - Treasury Yields & Spreads
  // ═══════════════════════════════════════════════════════════════════════════
  
  T10Y2Y: {
    id: "T10Y2Y",
    key: "T10Y2Y",
    label: "10Y-2Y Spread",
    category: "rates",
    defaultFrequency: "D",
    units: "Percent",
    notes: "Yield curve spread (10-year minus 2-year Treasury)",
  },
  
  DGS10: {
    id: "DGS10",
    key: "DGS10",
    label: "10-Year Treasury",
    category: "rates",
    defaultFrequency: "D",
    units: "Percent",
    notes: "10-Year Treasury Constant Maturity Rate",
  },
  
  DGS2: {
    id: "DGS2",
    key: "DGS2",
    label: "2-Year Treasury",
    category: "rates",
    defaultFrequency: "D",
    units: "Percent",
    notes: "2-Year Treasury Constant Maturity Rate",
  },
  
  T10YFF: {
    id: "T10YFF",
    key: "T10YFF",
    label: "10Y-Fed Funds Spread",
    category: "rates",
    defaultFrequency: "D",
    units: "Percent",
    notes: "10-Year Treasury minus Fed Funds Rate",
  },
  
  FEDFUNDS: {
    id: "FEDFUNDS",
    key: "FEDFUNDS",
    label: "Fed Funds Rate",
    category: "rates",
    defaultFrequency: "M",
    units: "Percent",
    notes: "Effective Federal Funds Rate (monthly average)",
  },
  
  EFFR: {
    id: "EFFR",
    key: "EFFR",
    label: "Effective Fed Funds",
    category: "rates",
    defaultFrequency: "D",
    units: "Percent",
    notes: "Effective Federal Funds Rate (daily)",
  },
  
  SOFR: {
    id: "SOFR",
    key: "SOFR",
    label: "SOFR",
    category: "rates",
    defaultFrequency: "D",
    units: "Percent",
    notes: "Secured Overnight Financing Rate",
  },
  
  DFEDTARU: {
    id: "DFEDTARU",
    key: "DFEDTARU",
    label: "Fed Funds Target Upper",
    category: "rates",
    defaultFrequency: "D",
    units: "Percent",
    notes: "Federal Funds Target Rate Upper Bound",
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // CREDIT - Credit Spreads & Risk Indicators
  // ═══════════════════════════════════════════════════════════════════════════
  
  HY_OAS: {
    id: "BAMLH0A0HYM2",
    key: "HY_OAS",
    label: "High Yield OAS",
    category: "credit",
    defaultFrequency: "D",
    units: "Percent",
    notes: "ICE BofA US High Yield Index Option-Adjusted Spread",
  },
  
  BAMLH0A0HYM2: {
    id: "BAMLH0A0HYM2",
    key: "BAMLH0A0HYM2",
    label: "High Yield OAS",
    category: "credit",
    defaultFrequency: "D",
    units: "Percent",
    notes: "ICE BofA US High Yield Index Option-Adjusted Spread",
  },
  
  BAA10Y: {
    id: "BAA10Y",
    key: "BAA10Y",
    label: "Baa Corporate Spread",
    category: "credit",
    defaultFrequency: "D",
    units: "Percent",
    notes: "Moody's Baa Corporate Bond Yield minus 10-Year Treasury",
  },
  
  IG_OAS: {
    id: "BAMLC0A0CM",
    key: "IG_OAS",
    label: "Investment Grade OAS",
    category: "credit",
    defaultFrequency: "D",
    units: "Percent",
    notes: "ICE BofA US Corporate Index Option-Adjusted Spread",
  },
  
  BAMLC0A0CM: {
    id: "BAMLC0A0CM",
    key: "BAMLC0A0CM",
    label: "Investment Grade OAS",
    category: "credit",
    defaultFrequency: "D",
    units: "Percent",
    notes: "ICE BofA US Corporate Index Option-Adjusted Spread",
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // FX - Dollar Indices
  // ═══════════════════════════════════════════════════════════════════════════
  
  DXY: {
    id: "DTWEXBGS",
    key: "DXY",
    label: "Broad Dollar Index",
    category: "fx",
    defaultFrequency: "D",
    units: "Index",
    notes: "Trade Weighted U.S. Dollar Index: Broad, Goods and Services",
  },
  
  DTWEXBGS: {
    id: "DTWEXBGS",
    key: "DTWEXBGS",
    label: "Broad Dollar Index",
    category: "fx",
    defaultFrequency: "D",
    units: "Index",
    notes: "Trade Weighted U.S. Dollar Index: Broad, Goods and Services",
  },
  
  DTWEXM: {
    id: "DTWEXM",
    key: "DTWEXM",
    label: "Major Currencies Dollar",
    category: "fx",
    defaultFrequency: "D",
    units: "Index",
    notes: "Trade Weighted U.S. Dollar Index: Major Currencies",
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // MONEY - Money Supply
  // ═══════════════════════════════════════════════════════════════════════════
  
  M2: {
    id: "M2SL",
    key: "M2",
    label: "M2 Money Stock",
    category: "money",
    defaultFrequency: "M",
    units: "Billions of Dollars",
    notes: "M2 Money Stock (seasonally adjusted)",
  },
  
  M2SL: {
    id: "M2SL",
    key: "M2SL",
    label: "M2 Money Stock",
    category: "money",
    defaultFrequency: "M",
    units: "Billions of Dollars",
    notes: "M2 Money Stock (seasonally adjusted)",
  },
  
  M2REAL: {
    id: "M2REAL",
    key: "M2REAL",
    label: "Real M2 Money Stock",
    category: "money",
    defaultFrequency: "M",
    units: "Billions of 1982-84 Dollars",
    notes: "Real M2 Money Stock (CPI-adjusted)",
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // INFLATION
  // ═══════════════════════════════════════════════════════════════════════════
  
  CPI: {
    id: "CPIAUCSL",
    key: "CPI",
    label: "CPI All Items",
    category: "inflation",
    defaultFrequency: "M",
    units: "Index 1982-84=100",
    notes: "Consumer Price Index for All Urban Consumers",
  },
  
  CPIAUCSL: {
    id: "CPIAUCSL",
    key: "CPIAUCSL",
    label: "CPI All Items",
    category: "inflation",
    defaultFrequency: "M",
    units: "Index 1982-84=100",
    notes: "Consumer Price Index for All Urban Consumers",
  },
  
  CORE_PCE: {
    id: "PCEPILFE",
    key: "CORE_PCE",
    label: "Core PCE",
    category: "inflation",
    defaultFrequency: "M",
    units: "Index 2017=100",
    notes: "Personal Consumption Expenditures Excluding Food and Energy",
  },
  
  PCEPILFE: {
    id: "PCEPILFE",
    key: "PCEPILFE",
    label: "Core PCE",
    category: "inflation",
    defaultFrequency: "M",
    units: "Index 2017=100",
    notes: "Personal Consumption Expenditures Excluding Food and Energy",
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // LABOR
  // ═══════════════════════════════════════════════════════════════════════════
  
  INITIAL_CLAIMS: {
    id: "ICSA",
    key: "INITIAL_CLAIMS",
    label: "Initial Claims",
    category: "labor",
    defaultFrequency: "W",
    units: "Number",
    notes: "Initial Claims for Unemployment Insurance",
  },
  
  ICSA: {
    id: "ICSA",
    key: "ICSA",
    label: "Initial Claims",
    category: "labor",
    defaultFrequency: "W",
    units: "Number",
    notes: "Initial Claims for Unemployment Insurance",
  },
  
  IC4WSA: {
    id: "IC4WSA",
    key: "IC4WSA",
    label: "4-Week MA Claims",
    category: "labor",
    defaultFrequency: "W",
    units: "Number",
    notes: "4-Week Moving Average of Initial Claims",
  },
  
  CONTINUED_CLAIMS: {
    id: "CCSA",
    key: "CONTINUED_CLAIMS",
    label: "Continued Claims",
    category: "labor",
    defaultFrequency: "W",
    units: "Number",
    notes: "Continued Claims (Insured Unemployment)",
  },
  
  CCSA: {
    id: "CCSA",
    key: "CCSA",
    label: "Continued Claims",
    category: "labor",
    defaultFrequency: "W",
    units: "Number",
    notes: "Continued Claims (Insured Unemployment)",
  },
  
  UNRATE: {
    id: "UNRATE",
    key: "UNRATE",
    label: "Unemployment Rate",
    category: "labor",
    defaultFrequency: "M",
    units: "Percent",
    notes: "Civilian Unemployment Rate",
  },
};

/**
 * Gets series configuration by key.
 * @param {string} key - Series key (e.g., "WALCL", "TGA", "DXY")
 * @returns {FredSeriesConfig|null} Series configuration or null if not found
 */
export function getSeriesConfig(key) {
  return FRED_SERIES_REGISTRY[key] || null;
}

/**
 * Gets the FRED series ID for a given key.
 * @param {string} key - Series key
 * @returns {string|null} FRED series ID or null if not found
 */
export function getSeriesId(key) {
  const config = getSeriesConfig(key);
  return config ? config.id : null;
}

/**
 * Lists all registered series configurations.
 * @param {function(FredSeriesConfig): boolean} [filterFn] - Optional filter function
 * @returns {FredSeriesConfig[]} Array of matching series configurations
 */
export function listSeriesConfigs(filterFn) {
  const configs = Object.values(FRED_SERIES_REGISTRY);
  return filterFn ? configs.filter(filterFn) : configs;
}

/**
 * Lists series by category.
 * @param {FredSeriesCategory} category - Category to filter by
 * @returns {FredSeriesConfig[]} Series in the specified category
 */
export function listSeriesByCategory(category) {
  return listSeriesConfigs((config) => config.category === category);
}

/**
 * Gets all unique FRED series IDs (deduped).
 * Useful for batch fetching.
 * @returns {string[]} Array of unique FRED series IDs
 */
export function getAllSeriesIds() {
  const ids = new Set(Object.values(FRED_SERIES_REGISTRY).map((c) => c.id));
  return Array.from(ids);
}

/**
 * Legacy compatibility: exports FRED_SERIES as a simple ID map.
 * Matches the shape of the original fredApi.js export.
 * @type {Record<string, string>}
 */
export const FRED_SERIES = Object.fromEntries(
  Object.entries(FRED_SERIES_REGISTRY).map(([key, config]) => [key, config.id])
);
